import React, { useCallback, useEffect, useRef, useState } from 'react';

import Image from 'next/image';

import useMediaQuery from '@/hooks/use-media-query';
import imageLoader from '@/utils/image-loader';
import { resolveSheetSnap } from '@/utils/utils';
import rushowlLogo from '@public/assets/logo/rushtrail-logo.png';

const EXPANDED_VIEWPORT_FRACTION = 0.86;
const SHEET_TRANSITION = 'height 0.4s cubic-bezier(0.32, 0.72, 0, 1)';

export default function HeaderContainer({
  peek,
  children,
  floating,
  footer,
}: {
  peek: React.ReactNode;
  children?: React.ReactNode;
  floating?: React.ReactNode;
  footer?: React.ReactNode;
}) {
  const { isMobile } = useMediaQuery();
  const [expanded, setExpanded] = useState(false);
  const [maxHeight, setMaxHeight] = useState(0);
  const [dragHeight, setDragHeight] = useState<number | null>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const expandableRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const dragStart = useRef<{ y: number; height: number; max: number } | null>(
    null
  );
  const didDrag = useRef(false);

  const computeMax = useCallback(() => {
    const sheet = sheetRef.current;
    const expandable = expandableRef.current;
    const content = contentRef.current;
    if (!sheet || !expandable || !content) return 0;
    const peekHeight = sheet.offsetHeight - expandable.offsetHeight;
    const available = window.innerHeight * EXPANDED_VIEWPORT_FRACTION - peekHeight;
    return Math.max(0, Math.min(content.offsetHeight, available));
  }, []);

  // Keep the expanded height in sync when live data changes the content size
  useEffect(() => {
    if (!isMobile || typeof ResizeObserver === 'undefined') return undefined;
    const content = contentRef.current;
    if (!content) return undefined;
    const observer = new ResizeObserver(() => {
      if (!expanded) return;
      const next = computeMax();
      setMaxHeight((prev) => (Math.abs(prev - next) > 1 ? next : prev));
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, [isMobile, expanded, computeMax]);

  const beginDrag = useCallback(
    (clientY: number) => {
      if (!isMobile) return;
      const expandable = expandableRef.current;
      if (!expandable) return;
      dragStart.current = {
        y: clientY,
        height: expandable.offsetHeight,
        max: computeMax(),
      };
      didDrag.current = false;
    },
    [computeMax, isMobile]
  );

  const moveDrag = useCallback((clientY: number) => {
    const start = dragStart.current;
    if (!start) return;
    const dy = start.y - clientY;
    if (Math.abs(dy) > 4) didDrag.current = true;
    setDragHeight(Math.max(0, Math.min(start.max, start.height + dy)));
  }, []);

  const endDrag = useCallback((clientY: number) => {
    const start = dragStart.current;
    dragStart.current = null;
    if (!start) return;
    if (!didDrag.current) {
      setDragHeight(null);
      return;
    }
    const dy = start.y - clientY;
    setMaxHeight(start.max);
    setExpanded(resolveSheetSnap(dy, start.height, start.max) === 'expanded');
    setDragHeight(null);
  }, []);

  const toggleExpanded = useCallback(() => {
    if (!isMobile || didDrag.current) return;
    setMaxHeight(computeMax());
    setExpanded((prev) => !prev);
  }, [computeMax, isMobile]);

  const expandIfCollapsed = useCallback(() => {
    if (expanded) return;
    toggleExpanded();
  }, [expanded, toggleExpanded]);

  // Pointer events cover mouse, trackpad, touch, and pen with one API —
  // touch-only handlers miss mouse drags at mobile widths on desktop.
  const dragProps = {
    onPointerDown: (event: React.PointerEvent) => {
      if (event.currentTarget.setPointerCapture) {
        try {
          event.currentTarget.setPointerCapture(event.pointerId);
        } catch {
          // capture is best-effort; dragging still works within the element
        }
      }
      beginDrag(event.clientY);
    },
    onPointerMove: (event: React.PointerEvent) => {
      moveDrag(event.clientY);
    },
    onPointerUp: (event: React.PointerEvent) => {
      endDrag(event.clientY);
    },
    onPointerCancel: (event: React.PointerEvent) => {
      endDrag(event.clientY);
    },
  };

  const dragSurfaceStyle = isMobile
    ? ({ touchAction: 'none', userSelect: 'none' } as React.CSSProperties)
    : undefined;

  return (
    <div
      ref={sheetRef}
      className="absolute inset-x-0 bottom-0 z-40 flex flex-col rounded-t-[24px] bg-white shadow-sheet lg:inset-y-0 lg:right-auto lg:w-[412px] lg:rounded-none lg:shadow-panel"
    >
      {floating ? (
        <div className="pointer-events-none absolute inset-x-0 -top-16 flex justify-center px-4 lg:hidden">
          <div className="pointer-events-auto">{floating}</div>
        </div>
      ) : null}

      <div className="hidden shrink-0 items-center gap-3.5 bg-ro-ink px-7 py-5 lg:flex">
        <div className="relative h-[46px] w-[46px] overflow-hidden rounded-xl bg-white/[0.06]">
          <Image
            loader={imageLoader}
            src={rushowlLogo}
            alt="RushTrail"
            layout="fill"
            objectFit="cover"
            objectPosition="center top"
            className="origin-top scale-[1.3]"
            priority
          />
        </div>
        <div className="flex flex-col leading-tight">
          <span className="text-base font-extrabold text-white">RushOwl</span>
          <span className="text-[12.5px] font-medium text-white/60">
            Live journey tracking
          </span>
        </div>
      </div>

      <button
        type="button"
        aria-expanded={expanded}
        aria-label={expanded ? 'Collapse trip details' : 'Expand trip details'}
        onClick={toggleExpanded}
        className="flex w-full cursor-grab justify-center pb-2 pt-3 lg:hidden"
        style={dragSurfaceStyle}
        {...dragProps}
      >
        <span className="h-[5px] w-[42px] rounded-full bg-ro-border" />
      </button>

      <div className="no-scrollbar min-h-0 flex-1 overflow-y-auto px-5 pb-5 lg:px-7 lg:pb-2 lg:pt-6">
        <div
          onClick={expandIfCollapsed}
          style={dragSurfaceStyle}
          {...dragProps}
        >
          {peek}
        </div>

        <div
          ref={expandableRef}
          className="no-scrollbar overflow-y-auto"
          style={
            isMobile
              ? {
                height: dragHeight ?? (expanded ? maxHeight : 0),
                transition: dragHeight !== null ? 'none' : SHEET_TRANSITION,
              }
              : undefined
          }
        >
          <div ref={contentRef} className="overflow-hidden">
            {children}
          </div>
        </div>
      </div>

      {footer}
    </div>
  );
}
